import { normalizeText, splitTerms } from '@/domain/pos/search-normalize';
import { OptionType } from '@/enums/OptionType';
import type { IItemOption } from '@/interfaces/IItemOption';
import type { IOption } from '@/interfaces/IOption';
import type { IOptionItem } from '@/interfaces/IOptionItem';
import { liveOptionItems } from './gpthelp-admin-menu-local';

/**
 * The option library as menu_getOptionGroups reads it (plan P1-4), pure:
 * the groups of the Opciones page (`GET /options`, every menu, live choices
 * paused ones included, live links to live products), filtered, ordered and
 * projected for the model.
 */

/** Choices and products shown per group in a list. */
export const OPTION_GROUPS_LIST_SAMPLE = 10;
/** Choices and products shown per group asked for by id. */
export const OPTION_GROUPS_DETAIL_MAX = 200;
/**
 * The serialized groups one answer carries: below the widget's 60,000-char
 * tool result cap, with room for the paging fields.
 */
export const OPTION_GROUPS_MAX_RESULT_CHARS = 50_000;

/** How a customer picks from the group (the editor's «¿Cómo se elige?»). */
export type TGpthelpGroupRuleKind = 'chooseOne' | 'chooseMany' | 'extras';

export interface IGpthelpGroupChoiceRead {
  choiceId: number;
  title: string;
  /** option_items.cost: a surcharge (0 = no charge), not a stock cost. */
  price: number;
  discountedPrice?: number;
  /** false = paused. */
  available: boolean;
  description?: string;
}

export interface IGpthelpGroupProductRead {
  itemId: number;
  name: string;
  categoryName: string | null;
  /** The group is off on this product (its link). */
  activeOnProduct?: false;
  /** The link has its own min, max, required or multiple. */
  ownRules?: true;
}

export interface IGpthelpOptionGroupRead {
  optionGroupId: number;
  title: string;
  description: string | null;
  menuId: number | null;
  rule: {
    kind: TGpthelpGroupRuleKind;
    required: boolean;
    min: number | null;
    max: number | null;
  };
  choices: IGpthelpGroupChoiceRead[];
  /** More choices than listed (a list shows the first ones). */
  choicesTruncated?: true;
  usedBy: {
    count: number;
    products: IGpthelpGroupProductRead[];
    truncated?: true;
  };
}

export interface IGpthelpOptionGroupFilters {
  /** Every word must appear in the group name or one of its choices. */
  query: string | null;
  /** Only the groups this product links. */
  itemId: number | null;
  /** Only the groups no product links. */
  unused: boolean;
}

/** A group with an id: the only ones the API returns and the model can name. */
export type TGpthelpLibraryGroup = IOption & { id: number };

type TLiveLink = IItemOption & { item: NonNullable<IItemOption['item']> };

const finiteOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const textOrNull = (value: string | null | undefined): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : null;

/** A rule limit: below 1 means none, as the editor reads it (`addonRuleKey`). */
const limitOrNull = (value: unknown): number | null => {
  const number = finiteOrNull(value);
  return number !== null && number >= 1 ? number : null;
};

/** The groups the model can name (the API always sends ids). */
export function libraryGroups(
  groups: readonly IOption[]
): TGpthelpLibraryGroup[] {
  return groups.filter(
    (group): group is TGpthelpLibraryGroup =>
      typeof group?.id === 'number' && !group.main && !group.deletedAt
  );
}

/** The group's links to live products (the API already drops the others). */
function liveLinks(group: IOption): TLiveLink[] {
  return (group.itemOptions ?? []).filter(
    (link): link is TLiveLink => !!link?.item && link.deleted !== true
  );
}

function linkItemId(link: TLiveLink): number {
  return link.itemId ?? link.item.id;
}

/** type add → extras; choose one → chooseOne; else chooseMany (plan P1-4). */
export function groupRuleKind(
  group: Pick<IOption, 'type' | 'multiple'>
): TGpthelpGroupRuleKind {
  if (group.type === OptionType.Add) return 'extras';
  return group.multiple ? 'chooseMany' : 'chooseOne';
}

/** The link overrides the group's rule (legacy per-product rules). */
function hasOwnRules(link: IItemOption): boolean {
  return [link.min, link.max, link.mandatory, link.multiple].some(
    (value) => value !== null && value !== undefined
  );
}

/** Every word of the query in the group name or its choices (case and accents ignored). */
function matchesQuery(group: IOption, terms: string[]): boolean {
  const haystack = normalizeText(
    [
      group.title ?? '',
      ...liveOptionItems(group).map((c) => c.title ?? ''),
    ].join(' ')
  );
  return terms.every((term) => haystack.includes(term));
}

/**
 * The groups matching the list filters, most used first (the Opciones
 * page's order), then by id so pages never overlap.
 */
export function filterOptionGroups(
  groups: readonly TGpthelpLibraryGroup[],
  filters: IGpthelpOptionGroupFilters
): TGpthelpLibraryGroup[] {
  const terms = filters.query !== null ? splitTerms(filters.query) : [];
  return groups
    .filter((group) => {
      const links = liveLinks(group);
      if (filters.unused && links.length > 0) return false;
      if (
        filters.itemId !== null &&
        !links.some((link) => linkItemId(link) === filters.itemId)
      )
        return false;
      return terms.length === 0 || matchesQuery(group, terms);
    })
    .sort((a, b) => liveLinks(b).length - liveLinks(a).length || a.id - b.id);
}

function choiceRead(
  choice: IOptionItem & { id: number }
): IGpthelpGroupChoiceRead {
  const discountedPrice = finiteOrNull(choice.discountedCost);
  const description = textOrNull(choice.description);
  return {
    choiceId: choice.id,
    title: choice.title ?? '',
    price: finiteOrNull(choice.cost) ?? 0,
    ...(discountedPrice !== null ? { discountedPrice } : {}),
    available: !!choice.active,
    ...(description !== null ? { description } : {}),
  };
}

function productRead(
  link: TLiveLink,
  categoryNameOf: (categoryId: number) => string | null
): IGpthelpGroupProductRead {
  return {
    itemId: linkItemId(link),
    name: link.item.name ?? '',
    categoryName: categoryNameOf(link.item.categoryId),
    ...(link.active === false ? { activeOnProduct: false as const } : {}),
    ...(hasOwnRules(link) ? { ownRules: true as const } : {}),
  };
}

/**
 * One group for the model: its rule, its first `max` choices and the first
 * `max` products that use it (by name), each list flagged when cut.
 */
export function optionGroupRead(
  group: TGpthelpLibraryGroup,
  options: {
    max: number;
    categoryNameOf: (categoryId: number) => string | null;
  }
): IGpthelpOptionGroupRead {
  const choices = liveOptionItems(group).filter(
    (choice): choice is IOptionItem & { id: number } =>
      typeof choice.id === 'number'
  );
  const products = liveLinks(group)
    .map((link) => productRead(link, options.categoryNameOf))
    .sort((a, b) => a.name.localeCompare(b.name) || a.itemId - b.itemId);

  return {
    optionGroupId: group.id,
    title: group.title ?? '',
    description: textOrNull(group.description),
    menuId: finiteOrNull(group.menuId),
    rule: {
      kind: groupRuleKind(group),
      required: !!group.mandatory,
      min: limitOrNull(group.min),
      max: limitOrNull(group.max),
    },
    choices: choices.slice(0, options.max).map(choiceRead),
    ...(choices.length > options.max
      ? { choicesTruncated: true as const }
      : {}),
    usedBy: {
      count: products.length,
      products: products.slice(0, options.max),
      ...(products.length > options.max ? { truncated: true as const } : {}),
    },
  };
}

/**
 * The reads of `groups` that fit `maxChars` once serialized, in order, and
 * the groups left out. The first group always comes back: when it alone is
 * too large, it shows fewer choices and products (still flagged as cut)
 * until it fits or shows one of each.
 */
export function fitGroupReads<T>(
  groups: readonly TGpthelpLibraryGroup[],
  options: {
    max: number;
    maxChars: number;
    read: (group: TGpthelpLibraryGroup, max: number) => T;
  }
): { reads: T[]; left: TGpthelpLibraryGroup[] } {
  const sizeOf = (read: T): number => JSON.stringify(read).length + 1;
  const firstRead = (group: TGpthelpLibraryGroup): T => {
    let max = options.max;
    let read = options.read(group, max);
    while (sizeOf(read) > options.maxChars && max > 1) {
      max = Math.max(1, Math.floor(max / 2));
      read = options.read(group, max);
    }
    return read;
  };

  const reads: T[] = [];
  let used = 0;
  for (const group of groups) {
    const read =
      reads.length === 0 ? firstRead(group) : options.read(group, options.max);
    const size = sizeOf(read);
    if (reads.length > 0 && used + size > options.maxChars) break;
    used += size;
    reads.push(read);
  }
  return { reads, left: groups.slice(reads.length) };
}
